import config from '../config';

const MARKETING_BASE = config.marketingApiBase;
const CUSTOMER_BASE = config.customerApiBase;
const CM_PREFIX = '/call-manager';

function authHeaders() {
  const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function apiFetch(base, path, options = {}) {
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      detail = body.detail || body.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === 'object' ? JSON.stringify(detail) : detail);
  }
  if (res.status === 204) return null;
  return res.json();
}

async function customerFetch(path, options = {}) {
  return apiFetch(CUSTOMER_BASE, path, options);
}

async function marketingFetch(path, options = {}) {
  return apiFetch(MARKETING_BASE, path, options);
}

/** Call Manager — customer API only (same pool as rest of customer service). */
async function callManagerFetch(path, options = {}) {
  return customerFetch(`${CM_PREFIX}${path}`, options);
}


export function memberRowTone(outcome) {
  const oc = String(outcome || '').toLowerCase();
  if (!oc || oc === 'pending') return 'neutral';
  if (oc === 'deleted') return 'deleted';
  if (oc === 'good_response' || oc === 'customer_created') return 'success';
  if (oc === 'lead') return 'lead';
  if (oc === 'follow_up') return 'followup';
  if (oc === 'not_contacted' || oc === 'no_answer' || oc === 'no_reply') return 'attempted';
  if (oc === 'opted_out' || oc === 'not_interested') return 'muted';
  if (oc === 'drop') return 'danger';
  return 'neutral';
}

export function opsEventTone(actionType, outcome) {
  const act = String(actionType || '').toLowerCase();
  const oc = String(outcome || '').toLowerCase();
  if (oc === 'good_response' || oc === 'customer_created' || act === 'create_customer') return 'success';
  if (oc === 'lead' || act === 'create_lead') return 'lead';
  if (oc === 'follow_up' || act === 'follow_up') return 'followup';
  if (oc === 'not_contacted' || act === 'not_contacted') return 'attempted';
  if (oc === 'opted_out' || act === 'opt_out') return 'muted';
  if (oc === 'drop' || act === 'drop') return 'danger';
  return 'neutral';
}

export function outcomeFromAction(action) {
  const act = String(action || '').toLowerCase();
  if (act === 'good_response') return { outcome: 'good_response', outcome_label: 'Good response' };
  if (act === 'follow_up') return { outcome: 'follow_up', outcome_label: 'Follow up' };
  if (act === 'create_lead') return { outcome: 'lead', outcome_label: 'Lead' };
  if (act === 'not_contacted') return { outcome: 'not_contacted', outcome_label: 'Not picked up' };
  if (act === 'opt_out') return { outcome: 'opted_out', outcome_label: 'Opted out' };
  if (act === 'create_customer') return { outcome: 'customer_created', outcome_label: 'Customer created' };
  if (act === 'drop') return { outcome: 'drop', outcome_label: 'Drop' };
  return null;
}

const IST_LOCALE = 'en-IN';
const IST_TZ = 'Asia/Kolkata';

/** Backend stores UTC as naive ISO — treat as UTC, display in IST. */
export function parseUtcIso(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const normalized = s.includes('T') ? `${s}Z` : `${s}T00:00:00Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Normalize customer-module phone (10-digit) for ops history / CM APIs. */
export function normalizePhoneForOpsHistory(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length >= 12 && digits.startsWith('91')) return `+${digits}`;
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `+91${digits.slice(1)}`;
  return digits.startsWith('+') ? String(raw).trim().replace(/\s/g, '') : `+${digits}`;
}

export function formatIstDateTime(value, { withSeconds = false } = {}) {
  const d = parseUtcIso(value);
  if (!d) return '—';
  return d.toLocaleString(IST_LOCALE, {
    timeZone: IST_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined,
    hour12: true,
  });
}

export function formatRelativeTime(isoString) {
  if (!isoString) return '—';
  const d = parseUtcIso(isoString);
  if (!d) return String(isoString).replace('T', ' ').slice(0, 16);
  const then = d.getTime();
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 45) return 'just now';
  if (sec < 3600) return `${Math.max(1, Math.round(sec / 60))} min ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)} hours ago`;
  if (sec < 604800) return `${Math.round(sec / 86400)} days ago`;
  return formatIstDateTime(isoString);
}

export async function fetchSfManualQueue({
  page = 1,
  pageSize = 10,
  completionSource = 'ops_escalated',
  escalated = false,
  startDate = null,
  endDate = null,
  opsOpenOnly = false,
  opsCompletedOnly = false,
  voiceTargetType = null,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
    escalated: String(escalated),
  });
  if (completionSource) params.set('completion_source', completionSource);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (opsOpenOnly) params.set('ops_open_only', 'true');
  if (opsCompletedOnly) params.set('ops_completed_only', 'true');
  if (voiceTargetType) params.set('voice_target_type', voiceTargetType);
  return callManagerFetch(`/manual-queue?${params}`);
}

export async function fetchSfBatchDetail(batchId, { page = 1, pageSize = 10, memberFilter = null } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (memberFilter) params.set('member_filter', memberFilter);
  return callManagerFetch(`/batches/${batchId}?${params}`);
}

export async function fetchRefollowStats() {
  return callManagerFetch('/refollow-stats');
}

export async function fetchRefollowBatches({ state = null } = {}) {
  const params = new URLSearchParams();
  if (state) params.set('state', state);
  const qs = params.toString();
  return callManagerFetch(qs ? `/refollow-batches?${qs}` : '/refollow-batches');
}

export async function fetchRefollowMembers(batchId, {
  page = 1,
  pageSize = 50,
  dueOnly = false,
  upcomingOnly = false,
  status = null,
  closedToday = false,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (dueOnly) params.set('due_only', 'true');
  if (upcomingOnly) params.set('upcoming_only', 'true');
  if (status) params.set('status', status);
  if (closedToday) params.set('closed_today', 'true');
  return callManagerFetch(`/refollow-batches/${batchId}/members?${params}`);
}

export async function fetchRefollowQueue(scope, {
  page = 1,
  pageSize = 50,
  sourceBatchId = null,
  signal = undefined,
} = {}) {
  const params = new URLSearchParams({
    scope: scope || 'due_today',
    page: String(page),
    page_size: String(pageSize),
  });
  if (sourceBatchId != null && sourceBatchId !== '') {
    params.set('source_batch_id', String(sourceBatchId));
  }
  const fetchOpts = signal ? { signal } : {};
  return callManagerFetch(`/refollow-queue?${params}`, fetchOpts);
}

export async function fetchRefollowCompletedDates({ page = 1, pageSize = 5 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  return callManagerFetch(`/refollow-completed/dates?${params}`);
}

export async function fetchRefollowCompletedByDate(
  date,
  { page = 1, pageSize = 8, status = 'all', sourceBatchId = null } = {},
) {
  const params = new URLSearchParams({
    date: String(date),
    page: String(page),
    page_size: String(pageSize),
  });
  if (status && status !== 'all') params.set('status', String(status));
  if (sourceBatchId != null && sourceBatchId !== '') {
    params.set('source_batch_id', String(sourceBatchId));
  }
  return callManagerFetch(`/refollow-completed?${params}`);
}

export async function createRefollowBatch(payload) {
  return callManagerFetch('/refollow-batches', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function publishRefollowBatch(batchId) {
  return callManagerFetch(`/refollow-batches/${batchId}/publish`, { method: 'POST' });
}

export async function patchRefollowMemberStatus(memberId, status) {
  return callManagerFetch(`/refollow-members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function deleteRefollowDraftMember(memberId) {
  return callManagerFetch(`/refollow-members/${memberId}`, { method: 'DELETE' });
}

export async function searchCallManagerMembers(query, { limit = 25 } = {}) {
  const q = String(query || '').trim();
  if (q.length < 2) return { items: [] };
  const params = new URLSearchParams({ q, limit: String(limit) });
  return callManagerFetch(`/members/search?${params}`);
}

export async function fetchCallManagerStats({ startDate = null, endDate = null, fresh = false, skipWa = false } = {}) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (fresh) params.set('fresh', '1');
  if (skipWa) params.set('skip_wa', '1');
  const qs = params.toString();
  return callManagerFetch(qs ? `/stats?${qs}` : '/stats');
}

export async function fetchCallManagerWaStats({ startDate = null, endDate = null, fresh = false } = {}) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (fresh) params.set('fresh', '1');
  const qs = params.toString();
  return callManagerFetch(qs ? `/stats/wa?${qs}` : '/stats/wa');
}

/** Sidebar badge — open batch counts only (no ops_calls / WA aggregation). */
export async function fetchCallManagerQueueSnapshot() {
  return callManagerFetch('/stats/summary');
}

/** Paginated drill-down: last status per phone in date window (or CM WA send rows). */
export async function fetchCallManagerStatsMembers({
  bucket,
  startDate = null,
  endDate = null,
  page = 1,
  pageSize = 20,
} = {}) {
  const params = new URLSearchParams();
  params.set('bucket', bucket);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  return callManagerFetch(`/stats/members?${params.toString()}`);
}

export async function fetchBatchActionProgress(batchId) {
  return callManagerFetch(`/batches/${batchId}/action-progress`);
}

export async function completeOpsBatch(batchId, { force = false } = {}) {
  return callManagerFetch(`/batches/${batchId}/complete-ops`, {
    method: 'POST',
    body: JSON.stringify({ force: !!force }),
  });
}

export async function reopenOpsBatch(batchId) {
  return callManagerFetch(`/batches/${batchId}/reopen-ops`, { method: 'POST' });
}

export function batchManualCompleteConfirmMessage(progress) {
  if (!progress) return 'Mark this batch completed?';
  const parts = [];
  const notPicked = progress.not_contacted_count || 0;
  const followUps = progress.follow_up_count || 0;
  if (notPicked > 0) {
    parts.push(`${notPicked} still didn't pick up`);
  }
  if (followUps > 0) {
    parts.push(`${followUps} follow-up${followUps === 1 ? '' : 's'} pending`);
  }
  if (!parts.length) return 'Mark this batch completed?';
  return `${parts.join(', ')}. Mark completed anyway? They will move to View Completed but stay visible for audit.`;
}

export async function postMemberAction(batchId, memberId, body) {
  return callManagerFetch(`/batches/${batchId}/members/${memberId}/action`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

const _waSentTs = (wa) => (wa?.sent_at ? Date.parse(wa.sent_at) : 0);

/** Keep optimistic WA / last_wa when a silent member reload has not caught up yet. */
export function mergeMemberWaFromPrevious(prev, incoming) {
  if (!incoming) return incoming;
  if (!prev) return incoming;
  const merged = { ...incoming };
  const prevAt = _waSentTs(prev.last_wa);
  const nextAt = _waSentTs(merged.last_wa);
  const serverHasWa = Boolean(
    merged.whatsapp_sent
    || merged.last_wa?.sent_at
    || merged.last_wa?.delivery_label
    || merged.last_wa?.status_label,
  );
  if (serverHasWa && nextAt && (!prevAt || nextAt >= prevAt)) {
    merged.whatsapp_sent = merged.whatsapp_sent ?? prev.whatsapp_sent ?? true;
    return merged;
  }
  if (prev.whatsapp_sent || prevAt || prev.last_wa?.delivery_label) {
    if (!serverHasWa || (prevAt && prevAt > nextAt)) {
      merged.last_wa = prev.last_wa ?? merged.last_wa;
      merged.whatsapp_sent = prev.whatsapp_sent ?? true;
    }
  }
  return merged;
}

/** Optimistic Last contacts row after a successful member WA send (refined on silent reload). */
/** Merge auto_whatsapp from member action / refollow done into row patch + green WA button. */
export function memberPatchFromAutoWhatsapp(actionResult) {
  const auto = actionResult?.auto_whatsapp;
  if (!auto) return null;
  if (auto.skipped_already_sent_today) {
    return { whatsapp_sent: true };
  }
  if (!auto.whatsapp_sent) return null;
  return memberLastWaPatchFromSendResult(auto, 'marketing');
}

export function memberLastWaPatchFromSendResult(result, templateMode = 'marketing') {
  const send = result?.send && typeof result.send === 'object' ? result.send : result;
  const kind =
    result?.wa_send_kind
    || send?.wa_send_kind
    || (result?.wa_fallback_used || send?.wa_fallback_used ? 'backup' : templateMode);
  const kindLabel =
    kind === 'backup'
      ? 'Utility backup'
      : kind === 'booking_quote'
        ? 'Booking quote'
        : kind === 'utility'
          ? 'Utility'
          : 'Marketing';
  const channel = kindLabel.toLowerCase();
  const delivery = 'WA sent';
  return {
    whatsapp_sent: true,
    last_wa: {
      delivery_label: delivery,
      channel_label: channel,
      status_label: `${delivery} · ${channel}`,
      kind_label: kindLabel,
      template_name: send?.template || result?.template || null,
      sent_at: new Date().toISOString(),
    },
  };
}

export async function sendMemberWhatsapp(
  batchId,
  memberId,
  templateMode = 'marketing',
  customerName = '',
  { refollowMemberId = null } = {},
) {
  const body = {
    template_mode: templateMode,
    customer_name: customerName || '',
  };
  if (refollowMemberId != null && refollowMemberId !== '') {
    body.refollow_member_id = refollowMemberId;
  }
  return callManagerFetch(`/batches/${batchId}/members/${memberId}/send-whatsapp`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function previewMemberBookingQuote(batchId, memberId, body) {
  if (!batchId || !memberId) {
    return customerFetch('/wa-booking-quote/preview', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }
  return callManagerFetch(`/batches/${batchId}/members/${memberId}/booking-quote/preview`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** Call Manager only — compose/preview on customer API; Meta send via customer → marketing bridge. */
export async function sendMemberBookingQuote(batchId, memberId, body) {
  const payload = body || {};
  try {
    return await customerFetch(`${CM_PREFIX}/batches/${batchId}/members/${memberId}/send-booking-quote`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const msg = String(err?.message || '');
    if (!/not found/i.test(msg) && !/\(404\)/.test(msg)) throw err;
    return customerFetch('/wa-booking-quote/send', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        send_source: 'cm_booking_quote',
        source_ref: memberId ? `batch_member_id=${memberId}` : payload.source_ref,
      }),
    });
  }
}

export async function deleteSegmentFlowBatchMember(batchId, phoneE164) {
  const params = new URLSearchParams({ phone: String(phoneE164 || '') });
  return callManagerFetch(`/batches/${batchId}/members?${params}`, { method: 'DELETE' });
}

export async function restoreSegmentFlowBatchMember(batchId, phoneE164) {
  const params = new URLSearchParams({ phone: String(phoneE164 || '') });
  return callManagerFetch(`/batches/${batchId}/members/restore?${params}`, { method: 'POST' });
}

export async function fetchMemberOpsHistory(phoneE164, { batchId, limit = 10, offset = 0 } = {}) {
  const phone = normalizePhoneForOpsHistory(phoneE164) || String(phoneE164 || '').trim();
  const params = new URLSearchParams({
    phone_e164: phone,
    limit: String(limit),
    offset: String(offset),
  });
  if (batchId) params.set('batch_id', String(batchId));
  return callManagerFetch(`/members/history?${params}`);
}

/** Status outcomes — row collapse + sheet activity semantics (not WhatsApp/call). */
export const STATUS_OUTCOMES = new Set([
  'good_response',
  'follow_up',
  'lead',
  'not_contacted',
  'opted_out',
  'customer_created',
  'drop',
  'deleted',
]);

export function hasStatusOutcome(outcome) {
  return STATUS_OUTCOMES.has(String(outcome || '').toLowerCase());
}

/** member_live rows historically lacked batch member id — merge from batch.members by phone. */
export function attachBatchMemberIds(memberLive, batchMembers = []) {
  if (!Array.isArray(memberLive)) return [];
  const idByPhone = new Map(
    (batchMembers || [])
      .filter((m) => m?.phone_e164 && m?.id != null)
      .map((m) => [m.phone_e164, m.id]),
  );
  return memberLive.map((m) => ({
    ...m,
    id: m.id ?? idByPhone.get(m.phone_e164) ?? null,
    batch_member_id: m.batch_member_id ?? m.id ?? idByPhone.get(m.phone_e164) ?? null,
  }));
}

const STATUS_OUTCOME_LABELS = {
  good_response: 'Good Response',
  follow_up: 'Follow Up',
  lead: 'Lead',
  not_contacted: 'Not Picked Up',
  opted_out: 'Opt Out',
  customer_created: 'Customer Created',
  drop: 'Drop',
  deleted: 'Deleted',
};

/** Short label for collapsed row chip, e.g. "Good Response ✓" (checkmark added in UI). */
export function statusOutcomeDisplayLabel(outcome, outcomeLabel) {
  const fromMember = (outcomeLabel || '').trim();
  if (fromMember && fromMember !== '—') {
    return fromMember.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const oc = String(outcome || '').toLowerCase();
  if (STATUS_OUTCOME_LABELS[oc]) return STATUS_OUTCOME_LABELS[oc];
  if (!oc) return '—';
  return oc.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** UI terminal: Call Manager grouping + badges. Backend batch completion uses stricter TERMINAL_OUTCOMES in member_profiles. */
export const TERMINAL_OUTCOMES = new Set([
  'good_response',
  'follow_up',
  'lead',
  'not_contacted',
  'customer_created',
  'not_interested',
  'opted_out',
  'drop',
  'picked_up',
  'no_answer',
  'skipped',
  'failed',
  'no_reply',
]);

const PENDING_TAB_OUTCOMES = new Set(['follow_up', 'not_contacted']);

/** Pending tab: untouched or follow-up / not picked up (still open work). */
export function isMemberStatusPending(outcome) {
  const oc = String(outcome || '').toLowerCase();
  if (PENDING_TAB_OUTCOMES.has(oc)) return true;
  return !oc || oc === 'pending' || oc === 'call_later' || oc === 'initiated' || oc === 'sent';
}

/** Actioned tab: terminal outcomes only (excludes follow-up / not picked up). */
export function isMemberStatusActioned(outcome) {
  const oc = String(outcome || '').toLowerCase();
  if (oc === 'deleted') return true;
  if (!oc || PENDING_TAB_OUTCOMES.has(oc)) return false;
  if (oc === 'pending' || oc === 'call_later' || oc === 'initiated' || oc === 'sent') return false;
  return isTerminalOutcome(outcome);
}

export function isTerminalOutcome(outcome) {
  return TERMINAL_OUTCOMES.has(String(outcome || '').toLowerCase());
}

export function isMutedMemberRow(outcome) {
  return memberRowTone(outcome) === 'muted';
}

export function outcomeBadgeTone(outcome) {
  const tone = memberRowTone(outcome);
  if (tone === 'success') return 'success';
  if (tone === 'lead') return 'lead';
  if (tone === 'followup') return 'followup';
  if (tone === 'attempted') return 'attempted';
  if (tone === 'muted' || tone === 'deleted') return 'muted';
  if (tone === 'danger') return 'danger';
  if (!outcome || String(outcome).toLowerCase() === 'pending') return 'pending';
  return 'neutral';
}

export function formatDomainCategory(member) {
  const cat = (member?.category || member?.matched_category || member?.matchedCategory || '').trim();
  const domain = (member?.domain || '').trim();
  if (domain && cat) return `${cat} · ${domain}`;
  return cat || domain || '—';
}

export function isNonWhatsappMember(row) {
  if (row?.is_non_whatsapp_lead === true) return true;
  if (row?.entity?.is_non_whatsapp_lead === true) return true;
  return !!(row?.non_whatsapp_lead_id);
}

export function batchVoiceTargetType(batch) {
  if (!batch) return 'on_whatsapp';
  const vt = (batch.voice_target_type || '').trim().toLowerCase();
  if (vt === 'not_on_whatsapp' || vt === 'non_whatsapp') return 'not_on_whatsapp';
  const snap = batch.filter_snapshot || {};
  const it = (snap.import_target || '').trim().toLowerCase();
  if (it === 'non_whatsapp') return 'not_on_whatsapp';
  return 'on_whatsapp';
}

export function isMarketingBotBatch(batch) {
  const src = (batch?.completion_source || '').trim().toLowerCase();
  return src === 'marketing_bot';
}

export function isNonWhatsappMarketingContext(member, batch) {
  if (!isMarketingBotBatch(batch)) return isNonWhatsappMember(member);
  if (batchVoiceTargetType(batch) === 'not_on_whatsapp') return true;
  return isNonWhatsappMember(member);
}

export function resolveNonWhatsappLeadId(member, batch) {
  const fromMember = member?.non_whatsapp_lead_id ?? member?.entity?.non_whatsapp_lead_id;
  if (fromMember) return Number(fromMember);
  if (isNonWhatsappMarketingContext(member, batch) && member?.lead_id) {
    return Number(member.lead_id);
  }
  return null;
}

export function normalizeCapturedWhatsapp(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return '';
}

export function formatCapturedWhatsappE164(raw) {
  const d = normalizeCapturedWhatsapp(raw);
  return d ? `+91${d}` : '';
}

export function canSendWhatsappForMember(member, batch) {
  if (!isNonWhatsappMarketingContext(member, batch)) return true;
  const cid = member?.customer_id ?? member?.entity?.customer_id;
  return !!cid;
}

export function isLeadOnlyMember(row) {
  if (row?.customer_id) return false;
  if (row?.entity?.customer_id) return false;
  if (String(row?.outcome || '').toLowerCase() === 'customer_created') return false;
  if (row?.is_lead_only === true) return true;
  if (row?.is_lead_only === false) return false;
  if (row?.entity?.is_lead_only === true) return true;
  if (row?.entity?.is_lead_only === false) return false;
  const leadId = row?.lead_id ?? row?.entity?.lead_id;
  const phone = (row?.phone_e164 || row?.phone || '').trim();
  return !!(leadId || phone);
}

export function memberRowToPanelContext(row) {
  const leadOnly = isLeadOnlyMember(row);
  const customerId = row?.customer_id ?? row?.entity?.customer_id ?? null;
  const leadId = row?.lead_id ?? row?.entity?.lead_id ?? null;
  const nw = isNonWhatsappMember(row);
  return {
    id: row?.id ?? row?.batch_member_id ?? null,
    batch_id: row?.batch_id ?? null,
    customer_id: customerId,
    lead_id: leadId,
    lead_customer_id: row?.lead_customer_id ?? null,
    is_lead_only: row?.is_lead_only ?? row?.entity?.is_lead_only ?? leadOnly,
    phone: row?.phone_e164 || row?.phone || '',
    phone_e164: row?.phone_e164 || row?.phone || '',
    dialed_phone: row?.dialed_phone || row?.non_wa_phone || '',
    non_wa_phone: row?.non_wa_phone || row?.dialed_phone || '',
    name: row?.name || row?.profile?.name || row?.voice_contact_name || '',
    business_name: row?.business_name || row?.voice_business_label || row?.name || '',
    domain: row?.domain || row?.profile?.domain || '',
    matched_category: row?.matched_category || row?.profile?.category || row?.matchedCategory || '',
    business_category: row?.category || row?.profile?.category || row?.business_category || '',
    address: row?.address || row?.profile?.address || '',
    city: row?.city || row?.profile?.city || '',
    pincode: row?.pincode || row?.profile?.pincode || '',
    preferred_language: row?.preferred_language || 'kannada',
    source: row?.source || '',
    scrape_name: row?.scrape_name || '',
    contact_status: row?.contact_status || '',
    voice_contact_name: row?.voice_contact_name || '',
    voice_business_label: row?.voice_business_label || '',
    star: !!row?.star,
    outcome: row?.outcome || null,
    outcome_label: row?.outcome_label || null,
    outcome_at: row?.outcome_at || null,
    is_non_whatsapp_lead: nw,
    non_whatsapp_lead_id: row?.non_whatsapp_lead_id ?? (nw && leadId ? leadId : null),
    captured_whatsapp_number: row?.captured_whatsapp_number || '',
    batch_voice_target: row?.batch_voice_target || '',
    batch_completion_source: row?.batch_completion_source || '',
  };
}

export async function resolveBusinessCategory(category, name = '') {
  const params = new URLSearchParams({ category });
  if (name) params.set('name', name);
  const qs = params.toString();
  try {
    return await customerFetch(`${CM_PREFIX}/resolve-category?${qs}`);
  } catch {
    return marketingFetch(`/resolve-category?${params}`);
  }
}

export async function fetchTaxonomyCategories(domain = '') {
  const qs = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  try {
    return await customerFetch(`${CM_PREFIX}/taxonomy/categories${qs}`);
  } catch {
    return marketingFetch(`/taxonomy/categories${qs}`);
  }
}

/** Payload for POST /customers/convert-from-lead from a batch member row. */
export function buildLeadConvertBody(member) {
  const leadId = member?.lead_id ? Number(member.lead_id) : null;
  const phone = (member?.phone_e164 || member?.phone || '').trim() || undefined;
  if (!leadId && !phone) return null;
  const body = {
    lead_id: leadId || undefined,
    phone,
    name: (member?.name || member?.voice_contact_name || '').trim() || undefined,
    business_name: (member?.business_name || member?.voice_business_label || member?.name || '').trim() || undefined,
    business_category: (member?.business_category || member?.category || member?.matched_category || '').trim() || undefined,
    domain: (member?.domain || '').trim() || undefined,
    matched_category: (member?.matched_category || '').trim() || undefined,
    address: (member?.address || '').trim() || undefined,
    city: (member?.city || '').trim() || undefined,
    pincode: (member?.pincode || '').trim() || undefined,
    preferred_language: member?.preferred_language || undefined,
    rating: member?.rating ?? 5,
    star: !!member?.star,
  };
  Object.keys(body).forEach((k) => {
    if (body[k] === undefined) delete body[k];
  });
  return body;
}

/** One-click convert from row — creates customer, logs ops action, returns customer_id. */
function apiErrorMessage(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
}

export function buildNonWhatsappConvertBody(member, leadDraft = {}) {
  const nwId = Number(member?.non_whatsapp_lead_id || member?.lead_id);
  if (!nwId) return null;
  const wa = normalizeCapturedWhatsapp(
    leadDraft?.captured_whatsapp_number ?? member?.captured_whatsapp_number,
  );
  if (!wa) return null;
  const body = {
    non_whatsapp_lead_id: nwId,
    whatsapp_phone: wa,
    name: (leadDraft?.name || member?.name || member?.voice_contact_name || '').trim() || undefined,
    business_name: (leadDraft?.business_name || member?.business_name || member?.voice_business_label || member?.name || '').trim() || undefined,
    business_category: (leadDraft?.business_category || member?.business_category || member?.category || member?.matched_category || '').trim() || undefined,
    domain: (leadDraft?.domain || member?.domain || '').trim() || undefined,
    matched_category: (leadDraft?.matched_category || member?.matched_category || '').trim() || undefined,
    address: (leadDraft?.address || member?.address || '').trim() || undefined,
    city: (leadDraft?.city || member?.city || '').trim() || undefined,
    pincode: (leadDraft?.pincode || member?.pincode || '').trim() || undefined,
    preferred_language: leadDraft?.preferred_language || member?.preferred_language || undefined,
    rating: leadDraft?.rating ?? member?.rating ?? 5,
    star: leadDraft?.star ?? !!member?.star,
  };
  Object.keys(body).forEach((k) => {
    if (body[k] === undefined) delete body[k];
  });
  return body;
}

export async function convertNonWhatsappMemberFromLead(
  member,
  leadDraft,
  {
    customerApiBase,
    authToken,
    batchId = null,
    marketingInitiated = false,
    postAction = true,
  } = {},
) {
  const body = buildNonWhatsappConvertBody(member, leadDraft);
  if (!body?.non_whatsapp_lead_id || !body?.whatsapp_phone) {
    throw new Error('Captured WhatsApp number and non-WA lead id required');
  }
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(`${customerApiBase}/customers/convert-from-non-whatsapp-lead`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(payload, `Convert failed (${res.status})`));
  }
  const data = await res.json();
  const customerId = Number(data.customer_id);
  if (!customerId) throw new Error('Convert succeeded but no customer_id returned');
  if (postAction && batchId && member?.id) {
    try {
      await postMemberAction(batchId, member.id, {
        action: 'create_customer',
        marketing_initiated: marketingInitiated,
      });
    } catch {
      /* audit best-effort */
    }
  }
  return { ...data, customer_id: customerId };
}

export async function convertMemberFromLead(
  member,
  {
    customerApiBase,
    authToken,
    batchId = null,
    marketingInitiated = false,
    postAction = true,
  } = {},
) {
  const body = buildLeadConvertBody(member);
  if (!body?.lead_id && !body?.phone) throw new Error('No lead_id or phone on this member');
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(`${customerApiBase}/customers/convert-from-lead`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(payload, `Convert failed (${res.status})`));
  }
  const data = await res.json();
  const customerId = Number(data.customer_id);
  if (!customerId) throw new Error('Convert succeeded but no customer_id returned');
  if (postAction && batchId && member?.id) {
    try {
      await postMemberAction(batchId, member.id, {
        action: 'create_customer',
        marketing_initiated: marketingInitiated,
      });
    } catch {
      /* audit best-effort */
    }
  }
  return { ...data, customer_id: customerId };
}

/** Human label for who last touched status (SF = marketing, CM = ops). Legacy blank role → CM. */
export function lastOpsRoleDisplayLabel(role) {
  const r = (role || '').trim().toLowerCase();
  if (r === 'marketing') return 'Marketing';
  if (r === 'ops') return 'Call Manager ops';
  if (!r) return 'Call Manager ops';
  return r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatLastOpsAttribution(member) {
  const role = (member?.last_ops_role || '').trim().toLowerCase();
  const at = member?.last_ops_at || member?.outcome_at;
  if (!role && !at) return null;
  const when = at ? formatMemberTimestamp(at) : '—';
  const who = lastOpsRoleDisplayLabel(role);
  return `Last: ${who} · ${when}`;
}

/** Primary last-action timestamp (outcome, else last ops touch). */
export function formatMemberLastActionMain(member) {
  return formatMemberTimestamp(member?.outcome_at || member?.last_ops_at);
}

/** Subline when ops/marketing attribution adds context without duplicating the main time. */
export function formatMemberLastActionSub(member) {
  const role = (member?.last_ops_role || '').trim().toLowerCase();
  const who = lastOpsRoleDisplayLabel(role);
  if (!role && !(member?.last_ops_at || member?.outcome_at)) return null;
  const outcomeAt = member?.outcome_at;
  const opsAt = member?.last_ops_at;
  if (outcomeAt && opsAt && String(outcomeAt) === String(opsAt)) {
    return `Last: ${who}`;
  }
  if (!outcomeAt && opsAt) {
    return `Last: ${who}`;
  }
  if (opsAt && outcomeAt && String(opsAt) !== String(outcomeAt)) {
    return `Last: ${who} · ${formatMemberTimestamp(opsAt)}`;
  }
  return formatLastOpsAttribution(member);
}

export function formatMemberTimestamp(value) {
  return formatIstDateTime(value);
}

/** YYYY-MM-DD → dd/mm/yy for Call Manager stats labels (API still uses ISO). */
export function formatStatsDisplayDate(isoDay) {
  const raw = String(isoDay || '').trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return raw || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Ops batch this refollow queue row came from (refollow_batches.source_batch_id). */
export function formatRefollowSourceBatchLine(row) {
  const bid = row?.source_batch_id ?? row?.ops_batch_id;
  const name = (row?.source_batch_name || '').trim();
  if (!bid && !name) return null;
  if (bid && name) return `Ops batch #${bid} · ${name}`;
  if (bid) return `Ops batch #${bid}`;
  return name;
}

export function formatMemberAddress(member) {
  const parts = [
    (member?.address || '').trim(),
    (member?.city || '').trim(),
    (member?.pincode || '').trim(),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export function memberBatchContextFromMember(member) {
  const vt = (member?.batch_voice_target || '').trim().toLowerCase();
  const nwBatch = vt === 'not_on_whatsapp' || vt === 'non_whatsapp' || member?.is_non_whatsapp_lead;
  return {
    completion_source: member?.batch_completion_source || 'marketing_bot',
    voice_target_type: nwBatch ? 'not_on_whatsapp' : vt,
    filter_snapshot: nwBatch ? { import_target: 'non_whatsapp' } : {},
  };
}

/** NW lead id for labels — prefer non_whatsapp_lead_id, else lead_id in NW batch before CRM. */
export function memberNonWhatsappLeadId(member, batch = null) {
  const fromMember = member?.non_whatsapp_lead_id ?? member?.entity?.non_whatsapp_lead_id;
  if (fromMember) return Number(fromMember);
  const b = batch || memberBatchContextFromMember(member);
  if (!member?.customer_id && isNonWhatsappMarketingContext(member, b) && member?.lead_id) {
    return Number(member.lead_id);
  }
  return null;
}

export function memberEntityLabel(member, batch = null) {
  const b = batch || memberBatchContextFromMember(member);
  const nwId = memberNonWhatsappLeadId(member, b);
  const showNw = nwId
    && !member?.customer_id
    && (isNonWhatsappMember(member) || isNonWhatsappMarketingContext(member, b));

  if (member?.customer_id) {
    return { type: 'customer', label: `Customer #${member.customer_id}` };
  }
  if (showNw) {
    return { type: 'non_whatsapp_lead', label: `Non-WA #${nwId}` };
  }
  if (isLeadOnlyMember(member)) {
    if (member?.lead_id) return { type: 'lead', label: `Lead #${member.lead_id}` };
    return { type: 'lead', label: 'Lead (not converted)' };
  }
  if (member?.lead_id) return { type: 'lead', label: `Lead #${member.lead_id}` };
  return { type: 'lead', label: 'Lead (not converted)' };
}

export function memberEntitySubtitleExtra(member, batch = null) {
  const entity = memberEntityLabel(member, batch);
  if (entity.type === 'non_whatsapp_lead') return ` · ${entity.label}`;
  if (entity.type === 'lead' && member?.lead_id) return ` · Lead #${member.lead_id}`;
  if (entity.type === 'customer' && member?.customer_id) return ` · Customer #${member.customer_id}`;
  return '';
}

/** Voice Tracker history for a phone — same store as marketing Voice Tracker. */
export async function fetchVoiceMktCustomerHistory(phone, { summaryOffset = 0, summaryLimit = 20 } = {}) {
  const digits = normalizePhoneForOpsHistory(phone);
  if (!digits) throw new Error('Missing phone');
  const base = (config.voiceMktApiBase || '').replace(/\/$/, '');
  const q = new URLSearchParams({
    summary_offset: String(summaryOffset),
    summary_limit: String(summaryLimit),
  });
  const res = await fetch(`${base}/history/${encodeURIComponent(digits)}?${q}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    let detail = `Voice history failed (${res.status})`;
    try {
      const body = await res.json();
      detail = body.detail || body.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function fetchVoiceMktCallDetail(logId) {
  const base = (config.voiceMktApiBase || '').replace(/\/$/, '');
  const res = await fetch(`${base}/calls/${encodeURIComponent(logId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    let detail = `Voice call detail failed (${res.status})`;
    try {
      const body = await res.json();
      detail = body.detail || body.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}
